/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 *
 * Reading an alert configuration's `enabledAlarmsKeys`.
 *
 * A fridge-alarm alert stores its selection as keys like `ca:26221`, `tk:100`, `m:18` — a
 * manufacturer prefix and the manufacturer's own alarm code. Users pick these by description on the
 * configuration screen ("100 - Heater Fan Failure (CR)"), so a panel showing `tk:100` shows nothing
 * a reader can act on.
 *
 * The descriptions come from Fresh's `FridgeAlarmCode` table, served by `/fresh/fridge-alarm-codes`
 * — no new data to maintain, and it is the same source the alarm dropdowns elsewhere in Insights
 * already use. What is *not* in any database is the mapping below: the alert-config key prefix and
 * Fresh's `FridgeMake` letter are two different encodings of the same five manufacturers, and
 * nothing joins them.
 *
 * That mapping is a genuine footgun, because the two encodings very nearly invert:
 *
 *     prefix   CouchDB fridgeMake   Fresh FridgeMake   shown as
 *     ca       carrier-advanced     A                  Carrier Advanced
 *     c        carrier-basic        S                  Carrier Standard
 *     cb       cargobull            C                  Cargobull
 *     tk       thermo-king          T                  Thermo King
 *     m        magnum               M                  Magnum
 *
 * Note `c` → `S` and `cb` → `C`: reading Fresh's `C` as "carrier" would silently relabel all 109
 * Cargobull alarms as Carrier and all 20 Carrier Standard alarms as Cargobull.
 *
 * The mapping was not assumed — it was derived by partitioning a real 910-key configuration against
 * the alarm catalogue: each prefix's code set falls inside exactly one manufacturer's, and the five
 * counts (465 / 311 / 109 / 20 / 5) add to 910 with nothing left over.
 */

/** A manufacturer, in all three of the spellings this feature has to reconcile. */
export interface AlarmMake {
  /** The key prefix used in `enabledAlarmsKeys`. */
  prefix: string
  /** Fresh's single-letter `FridgeMake`, used to join against `/fresh/fridge-alarm-codes`. */
  freshMake: string
  /** What the alert configuration screen calls it. */
  label: string
}

/**
 * Ordered as the configuration screen groups them, so the panel and the screen read alike.
 */
export const ALARM_MAKES: AlarmMake[] = [
  { prefix: 'ca', freshMake: 'A', label: 'Carrier Advanced' },
  { prefix: 'c', freshMake: 'S', label: 'Carrier Standard' },
  { prefix: 'tk', freshMake: 'T', label: 'Thermo King' },
  { prefix: 'm', freshMake: 'M', label: 'Magnum' },
  { prefix: 'cb', freshMake: 'C', label: 'Cargobull' },
]

const BY_PREFIX = new Map(ALARM_MAKES.map((make) => [make.prefix, make]))

/**
 * Alarms the catalogue is missing.
 *
 * Fresh holds 909 of the 910 alarms the alert system recognises. The one it lacks is genuinely
 * referenced by live configurations, so it is named here rather than left to render as a bare key.
 * Anything else unresolved still degrades to its key — this is a patch for a known gap, not a
 * second catalogue.
 */
const MISSING_FROM_FRESH: Record<string, string | undefined> = {
  'A:998': 'Engine getting hot',
}

export interface ParsedAlarm {
  /** The original key, e.g. `tk:100`. */
  key: string
  make?: AlarmMake
  code: number
  /** The lookup key into the Fresh catalogue, e.g. `T:100`. Absent for an unrecognised prefix. */
  freshKey?: string
}

/**
 * Split one alarm key. Returns a parse even when the prefix is unrecognised, so a manufacturer
 * added after this build still renders as itself rather than vanishing.
 */
export const parseAlarmKey = (key: string): ParsedAlarm => {
  const separator = key.lastIndexOf(':')
  const prefix = separator === -1 ? '' : key.slice(0, separator)
  // `Number('')` is 0, not NaN, so an empty code segment would otherwise parse as alarm zero — a
  // real code for several manufacturers, and the wrong answer for a key that carries none.
  const codeText = key.slice(separator + 1)
  const code = codeText === '' ? Number.NaN : Number(codeText)
  const make = BY_PREFIX.get(prefix)

  return {
    key,
    make,
    code: Number.isFinite(code) ? code : Number.NaN,
    freshKey: make && Number.isFinite(code) ? `${make.freshMake}:${String(code)}` : undefined,
  }
}

/** One alarm, ready to display. */
export interface DescribedAlarm extends ParsedAlarm {
  /** The manufacturer's description, when the catalogue knows it. */
  description?: string
}

export const describeAlarm = (key: string, catalogue: Map<string, string>): DescribedAlarm => {
  const parsed = parseAlarmKey(key)
  const description = parsed.freshKey
    ? (catalogue.get(parsed.freshKey) ?? MISSING_FROM_FRESH[parsed.freshKey])
    : undefined

  return { ...parsed, description }
}

/** A manufacturer's alarms, for one group of the panel. */
export interface AlarmGroup {
  label: string
  prefix: string
  alarms: DescribedAlarm[]
}

/**
 * Group alarm keys by manufacturer, in the screen's order, dropping manufacturers with nothing
 * selected. Alarms sort by code, matching the checkbox list a user picked them from.
 *
 * Keys with an unrecognised prefix are gathered under one trailing group rather than discarded: a
 * selection the panel cannot name is still a selection the reader needs to see.
 */
export const groupAlarms = (keys: unknown, catalogue: Map<string, string>): AlarmGroup[] => {
  if (!Array.isArray(keys)) return []

  const described = keys
    .filter((key): key is string => typeof key === 'string')
    .map((key) => describeAlarm(key, catalogue))

  const groups: AlarmGroup[] = []

  for (const make of ALARM_MAKES) {
    const alarms = described.filter((alarm) => alarm.make?.prefix === make.prefix)
    if (alarms.length > 0) {
      groups.push({ label: make.label, prefix: make.prefix, alarms: alarms.sort((a, b) => a.code - b.code) })
    }
  }

  const unknown = described.filter((alarm) => !alarm.make)
  if (unknown.length > 0) groups.push({ label: 'Other manufacturers', prefix: '', alarms: unknown })

  return groups
}

/** How an alarm reads on one line: `100 – Heater Fan Failure (CR)`, or the key if unresolved. */
export const alarmText = (alarm: DescribedAlarm): string => {
  if (!Number.isFinite(alarm.code)) return alarm.key
  return alarm.description ? `${String(alarm.code)} – ${alarm.description}` : `${String(alarm.code)} – ${alarm.key}`
}

/** The membership difference between two selections, as the alarms that came and went. */
export const diffAlarmKeys = (before: unknown, after: unknown): { added: string[]; removed: string[] } => {
  const b = new Set(Array.isArray(before) ? before.filter((k): k is string => typeof k === 'string') : [])
  const a = new Set(Array.isArray(after) ? after.filter((k): k is string => typeof k === 'string') : [])

  return {
    added: [...a].filter((key) => !b.has(key)),
    removed: [...b].filter((key) => !a.has(key)),
  }
}
